import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { formatUsd } from "@veyso/core";
import { api, type BillingView } from "../api.js";

/**
 * Dispatched on `window` when something on the page has changed the balance,
 * so the chip reads it again: `window.dispatchEvent(new Event(BILLING_CHANGED))`.
 */
export const BILLING_CHANGED = "veyso:billing-changed";

/**
 * What is left, in the top bar.
 *
 * `Free 2/3` while any of the free videos remain, then the balance. One or the
 * other rather than both — somebody on the free tier has no balance to speak
 * of, and somebody who has paid does not care how many free videos they used
 * months ago.
 *
 * It is a link to the billing settings, which is the only useful thing to do
 * with the information. And it is on the person's side of the bar, next to the
 * account menu, because that is where the ordering comment in AppShell says
 * things about you go — what the app is doing lives on the left.
 *
 * Renders nothing at all on a deployment that does not bill, which is every
 * self-hosted install and every `docker compose up`.
 */
export const BillingChip: React.FC = () => {
  const [view, setView] = useState<BillingView | null>(null);
  const onBilling = useLocation().hash === "#billing";

  useEffect(() => {
    // On mount, and again when something on the page says the balance moved.
    // It changes when somebody exports, tops up or asks for a launch video
    // version; the first two reload the screen they happen on, and the studio
    // announces the third. Polling the bar every few seconds to catch a number
    // that moves that rarely is not worth the request.
    const load = () => {
      void api
        .billing()
        .then(setView)
        .catch(() => {
          // A bar that cannot report the balance says nothing rather than
          // showing an error where a number goes.
        });
    };
    load();
    window.addEventListener(BILLING_CHANGED, load);
    return () => window.removeEventListener(BILLING_CHANGED, load);
  }, []);

  if (!view || !view.billing) return null;

  // A free-forever team has nothing to count down and nothing to top up.
  const freeForever = view.freeForever === true;
  const onFreeTier = view.freeRemaining > 0;
  const label = freeForever
    ? "Free forever"
    : onFreeTier
      ? `Free ${view.freeVideoLimit - view.freeRemaining}/${view.freeVideoLimit}`
      : formatUsd(view.balanceUsd);

  // Nothing left to spend. Said in the accent rather than in red: it is a thing
  // to do, not a thing that has gone wrong.
  const empty = !freeForever && !onFreeTier && view.balanceUsd <= 0;

  return (
    <Link
      to="/settings#billing"
      // Already there: still shown, because it is the number the page is about,
      // but not a link to where you are.
      onClick={(event) => onBilling && event.preventDefault()}
      className={`tnum flex items-center gap-1.5 rounded-[6px] border px-2.5 py-1 text-[12px] transition-colors ${
        empty
          ? "border-ember-500/40 bg-ember-500/10 text-ember-600 hover:bg-ember-500/15"
          : "border-[var(--edge)] bg-bay-850 text-ink-200 hover:bg-bay-800"
      }`}
      title={
        freeForever
          ? "Everything this team makes is free"
          : onFreeTier
            ? `${view.freeRemaining} of your ${view.freeVideoLimit} free videos left`
            : `${formatUsd(view.balanceUsd)} of credit`
      }
    >
      {empty ? "Add credit" : label}
    </Link>
  );
};
